import { motion } from 'motion/react';
import { useTranslation } from 'react-i18next';

import { EmailCard } from '@/components/ui/email-card';
import type { EmailItem } from '@/config/office/types';

import { emailItemLayoutId, emailItemTransition, filterOptionLabelClass } from './constants';

interface SelectedEmailsListProps {
  emails: Array<EmailItem>;
  onToggleEmail: (id: string) => void;
  sectionRef: React.RefObject<HTMLDivElement | null>;
  showCheckboxes: boolean;
}

export function SelectedEmailsList({
  emails,
  onToggleEmail,
  sectionRef,
  showCheckboxes,
}: SelectedEmailsListProps) {
  const { t } = useTranslation();

  if (emails.length === 0) {
    return null;
  }

  return (
    // No initial/animate fade here: the section mounts the instant the first
    // email is selected, and an opacity-0 parent would hide the start of the
    // card's shared-layoutId morph from the Inbox list. The exit fade (via the
    // AnimatePresence wrapper in CustomFilterContent) is fine — the morphing
    // card has already left this section by then.
    <motion.div exit={{ opacity: 0 }} layout transition={emailItemTransition}>
      <div className="px-3 pt-2 pb-1" ref={sectionRef}>
        <h3 className={`mb-1 ${filterOptionLabelClass}`}>
          {t('assistant.emailContext.selectedEmails')}
        </h3>
      </div>

      <div className="flex flex-col gap-1 px-3 pb-3">
        {emails.map((email) => (
          <motion.div
            key={email.id}
            layout
            layoutId={emailItemLayoutId(email.id)}
            transition={emailItemTransition}
          >
            <EmailCard
              id={email.id}
              sender={email.sender}
              senderColor={email.senderColor}
              senderInitial={email.senderInitial}
              subject={email.subject}
              time={email.time}
              {...(email.snippet && { snippet: email.snippet })}
              isDisabled={false}
              isSelected={true}
              onClick={() => onToggleEmail(email.id)}
              showCheckboxes={showCheckboxes}
            />
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
}
